import { Controller } from "@hotwired/stimulus"

// Chat providers only (mirrors Unipile::Accounts::MESSAGING_PROVIDERS) — the
// only channels where a "no chat yet" contact or a freeform raw identifier
// can start a brand-new conversation via /composer/start_chat. Never
// "taskjane" (internal, contact-only) or "email" (the mailbox, out of scope).
const MESSAGING_CHANNELS = ["linkedin", "whatsapp", "instagram", "telegram"]

// Universal messenger layer over the Jane composer (home bar): the identity
// chip becomes a destination picker (Jane / a person on a channel / an
// audience). Jane sends keep assistant-compose's flow untouched; any other
// destination swaps the form's target + field name and submits via fetch so
// the user never leaves home. Rides on the same form as assistant-compose —
// this controller owns everything that ISN'T Jane.
export default class extends Controller {
    static targets = ["field", "destChip", "destAvatar", "destLabel", "audienceToken",
                      "recipientWrap", "recipientChip", "recipientPanel", "recipientInput",
                      "recipientResults", "sendBtn", "status", "janeOnly"]
    static values = { recipientsUrl: String, janeAction: String, personaUrl: String, janeChipHtml: String, prefill: Object }

    connect() {
        this._dest = { kind: "jane" }
        this._recipient = null
        this._janeChip = { className: this.destChipTarget.className, html: this.destChipTarget.innerHTML }
        this._debounce = null
        // Bumped on every fetch kicked off, and again whenever the input drops
        // back below the search threshold — a response only renders if it's
        // still the most recently issued request, so a slow/out-of-order
        // response can never clobber what the user is looking at now.
        this._requestSeq = 0
        this._suggestionsHtml = ""

        // Contact-card / ⌘K entry point (#463): a destination already chosen
        // server-side (Messaging::Destinations' default for that contact)
        // arrives as JSON on the form. Append-only — primes the same state
        // selectDestination()/pickRecipient() maintain, then repaints via the
        // existing helpers, so it can't diverge from a normal manual pick.
        if (this.prefillValue && this.prefillValue.channel) {
            this._dest = {
                kind: "channel",
                channel: this.prefillValue.channel,
                label: this.prefillValue.label,
                icon: this.prefillValue.icon,
                css: this.prefillValue.css
            }
            this._recipient = {
                name: this.prefillValue.name,
                url: this.prefillValue.url,
                avatar: this.prefillValue.avatar
            }
            this._syncDestination()
            this._paintRecipientChip()
            this.element.action = this.prefillValue.url
        }
        this._syncDestinationChecks()
    }

    // --- destination picking -------------------------------------------------

    get assistant() { return this.application.getControllerForElementAndIdentifier(this.element, "assistant-compose") }

    preserveDraft(event) {
        if (event && (this.assistant?.hasStagedAttachments || this.assistant?.isBusy)) {
            event.preventDefault()
            this._showStatus("Finish the current message or remove attachments before leaving this chat.", null, true)
            return
        }
        this.assistant?.saveDraft()
    }

    async selectPersona(event) {
        if (this.switchingPersona || this.assistant?.isBusy || this.element.classList.contains("is-sending")) return
        const id = event.currentTarget.dataset.personaId || ""
        if (id === (this.element.dataset.composerPersonaId || "") && this.element.dataset.composerPurpose === "chat") {
            this.selectDestination(event)
            return
        }
        if (this.assistant?.hasStagedAttachments) {
            this._showStatus("Send or remove attachments before switching personas.", null, true)
            return
        }
        this.preserveDraft()
        this.switchingPersona = true
        const body = new FormData()
        body.set("resume", "1")
        body.set("purpose", "chat")
        if (id) body.set("persona_id", id)
        const project = this.element.querySelector('[name="project_id"]')?.value
        if (project) body.set("project_id", project)
        try {
            const response = await fetch(this.personaUrlValue, {
                method: "POST", credentials: "same-origin", body,
                headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" }
            })
            const data = await response.json().catch(() => ({}))
            if (!this.element.isConnected) return
            if (!response.ok || !data.ok) {
                this._showStatus(data.error || "Couldn't switch personas. Your draft is saved.", null, true)
                return
            }
            // In the Jane sidebar the new persona's thread opens in place; a
            // visit would leave the page the sidebar sits beside.
            if (data.conversation_id && this.element.closest(".detail-sidebar--jane")) {
                document.dispatchEvent(new CustomEvent("jane-sidebar:conversation", { detail: { conversationId: data.conversation_id } }))
                return
            }
            if (window.Turbo) window.Turbo.visit(data.url)
            else window.location.href = data.url
        } catch {
            this._showStatus("Couldn't switch personas. Your draft is saved.", null, true)
        } finally { this.switchingPersona = false }
    }

    _syncDestinationChecks() {
        this.element.querySelectorAll('.composer-focused-menu [data-kind]').forEach(row => {
            const selected = row.dataset.kind === this._dest.kind && (this._dest.kind === "jane" ?
                (row.dataset.personaId || "") === (this.element.dataset.composerPersonaId || "") :
                this._dest.kind === "channel" ? row.dataset.channel === this._dest.channel : row.dataset.token === this._dest.token)
            row.setAttribute("aria-checked", String(selected))
            const check = row.querySelector(".composer-destination-check")
            if (check) check.hidden = !selected
        })
    }

    selectDestination(e) {
        const d = e.currentTarget.dataset
        if (d.kind === "jane") {
            this._dest = { kind: "jane" }
        } else if (d.kind === "audience") {
            this._dest = { kind: "audience", token: d.token, label: d.label, emoji: d.emoji, icon: d.icon }
        } else {
            this._dest = { kind: "channel", channel: d.channel, label: d.label, icon: d.icon, css: d.css }
            this._recipient = null
        }
        this._syncDestination()
        this.fieldTarget.focus()
    }

    _syncDestination() {
        const jane = this._dest.kind === "jane"
        const channel = this._dest.kind === "channel"

        // The chip mirrors the pick; Jane restores her identity chip verbatim.
        if (jane) {
            this.destChipTarget.className = this._janeChip.className
            this.destChipTarget.innerHTML = this._janeChip.html
        } else if (channel) {
            this._paintChip(`composer-chip composer-chip--${this._dest.css}`,
                `<i class="bi ${this._dest.icon}"></i>`, this._dest.label)
        } else {
            this._paintChip("composer-chip composer-chip--internal",
                this._dest.icon ? `<i class="bi ${this._escape(this._dest.icon)}"></i>` : `<span class="composer-dest-emoji">${this._escape(this._dest.emoji)}</span>`, this._dest.label)
        }

        this._syncDestinationChecks()

        // Jane-only chrome (+ menu, mic send, search tag) yields to the plain
        // send when the message leaves Jane's lane.
        this.janeOnlyTargets.forEach((el) => { el.hidden = !jane })
        if (this.hasSendBtnTarget) this.sendBtnTarget.hidden = jane

        this.recipientWrapTarget.hidden = !channel
        if (!channel) this._closeRecipientPanel()
        if (this.hasAudienceTokenTarget) this.audienceTokenTarget.value = this._dest.kind === "audience" ? this._dest.token : ""

        // Field identity per destination: Jane's endpoint reads `message`,
        // person/audience endpoints read `body`.
        this.fieldTarget.name = jane ? "message" : "body"
        this.element.action = jane ? this.janeActionValue
            : (this._dest.kind === "audience" ? this.element.dataset.universalComposeReflectionsUrl : (this._recipient?.url || ""))

        this._syncPlaceholder()
        this._syncSend()
        this._clearStatus()
    }

    _paintChip(className, iconHtml, label) {
        this.destChipTarget.className = `${className} composer-chip--picker`
        this.destChipTarget.innerHTML = `${iconHtml}<span>${this._escape(label)}</span><i class="bi bi-chevron-down composer-chip-caret" aria-hidden="true"></i>`
    }

    _syncPlaceholder() {
        let override = null
        if (this._dest.kind === "channel") {
            const name = this._recipient?.name
            override = name ? `Message ${name} on ${this._dest.label}…` : `Message someone on ${this._dest.label}…`
            if (this._dest.channel === "taskjane") override = name ? `Message ${name} privately…` : "Message a connection privately…"
        } else if (this._dest.kind === "audience") {
            override = `Share with ${this._dest.label.toLowerCase()}…`
        }
        if (override) {
            this.fieldTarget.dataset.placeholderOverride = override
            this.fieldTarget.placeholder = override
        } else {
            delete this.fieldTarget.dataset.placeholderOverride
            this.fieldTarget.placeholder = "Ask me anything…"
        }
    }

    // --- recipient picking ----------------------------------------------------

    toggleRecipient() {
        if (this.recipientPanelTarget.hidden) {
            this.recipientPanelTarget.hidden = false
            this.recipientInputTarget.value = ""
            this._suggestionsHtml = ""
            // Opens straight onto the 5 most recent people for this channel
            // instead of a blank panel — same endpoint, just no `q`.
            this._fetchRecipients("")
            this.recipientInputTarget.focus()
        } else {
            this._closeRecipientPanel()
        }
    }

    searchRecipients() {
        clearTimeout(this._debounce)
        const q = this.recipientInputTarget.value.trim()
        if (q.length < 2) {
            // Below the search threshold: fall back to the suggestions already
            // fetched on open, and invalidate any in-flight search fetch so a
            // late response can't overwrite them once it lands.
            this._requestSeq++
            this.recipientResultsTarget.innerHTML = this._suggestionsHtml
            return
        }
        this._debounce = setTimeout(() => this._fetchRecipients(q), 180)
    }

    _fetchRecipients(q) {
        const seq = ++this._requestSeq
        const suggested = q.length < 2
        this._renderLoading()
        const url = `${this.recipientsUrlValue}?q=${encodeURIComponent(q)}&channel=${encodeURIComponent(this._dest.channel)}`
        fetch(url, { headers: { "Accept": "application/json" }, credentials: "same-origin" })
            .then((r) => (r.ok ? r.json() : { recipients: [] }))
            .then((data) => {
                if (seq !== this._requestSeq) return // a newer request has since been issued — stale, drop it
                this._renderRecipients(data.recipients || [], suggested)
            })
            .catch(() => {
                if (seq !== this._requestSeq) return
                this._renderRecipients([], suggested)
            })
    }

    _renderLoading() {
        this.recipientResultsTarget.innerHTML = `<p class="composer-recipient-loading">Searching…</p>`
    }

    _renderRecipients(rows, suggested = false) {
        if (!rows.length) {
            if (suggested) {
                // No recent people for this channel yet — a blank panel reads
                // better than an error-toned empty state for a group that's
                // just... empty.
                this.recipientResultsTarget.innerHTML = ""
                this._suggestionsHtml = ""
                return
            }
            if (this._freeformEligible()) {
                this.recipientResultsTarget.innerHTML = this._freeformRow(this.recipientInputTarget.value.trim())
                return
            }
            this.recipientResultsTarget.innerHTML = `<p class="composer-recipient-none">No one reachable on ${this._escape(this._dest.label)} matches.</p>`
            return
        }
        const label = suggested ? `<p class="composer-recipient-group-label">Recent</p>` : ""
        const html = label + rows.map((r) => this._recipientRowHtml(r)).join("")
        if (suggested) this._suggestionsHtml = html
        this.recipientResultsTarget.innerHTML = html
    }

    _recipientRowHtml(r) {
        const dest = (r.destinations || []).find((d) => d.channel === this._dest.channel)
        if (!dest) return ""
        const face = r.avatar_url
            ? `<img class="composer-recipient-face" src="${this._escape(r.avatar_url)}" alt="" referrerpolicy="no-referrer">`
            : `<span class="composer-recipient-face composer-recipient-face--initial">${this._escape((r.name || "?")[0])}</span>`
        const meta = r.meta ? `<span class="composer-recipient-meta">${this._escape(r.meta)}</span>` : ""
        // A `new: true` destination has no synced chat yet — Messaging::StartChat
        // will create one on first send, so there's no url to swap the form to.
        const hint = dest.new ? `<span class="composer-recipient-hint">new conversation</span>` : ""
        const rowClass = `composer-recipient-row${dest.new ? " composer-recipient-row--new" : ""}`
        const targetAttrs = dest.new
            ? `data-new="true" data-contact-id="${r.id}"`
            : `data-url="${this._escape(dest.url)}"`
        return `<button type="button" class="${rowClass}" data-action="universal-compose#pickRecipient"
            data-name="${this._escape(r.name)}" ${targetAttrs} data-avatar="${this._escape(r.avatar_url || "")}">
            ${face}<span class="composer-recipient-name">${this._escape(r.name)}</span>${meta}${hint}</button>`
    }

    // A raw identifier (a phone number or a bare handle) typed into the
    // recipient search that matched no contact can still start a brand-new
    // conversation, but only on an actual chat provider — never the internal
    // "taskjane" channel (no such thing as a handle there) or email (out of
    // scope for the messenger's start-chat path).
    _freeformEligible() {
        if (!MESSAGING_CHANNELS.includes(this._dest.channel)) return false

        return this._looksLikeIdentifier(this.recipientInputTarget.value.trim())
    }

    _looksLikeIdentifier(q) {
        if (/^[+\d][\d\s().-]{2,}$/.test(q)) return true // phone-shaped
        if (/^[\w.+-]{3,}$/.test(q)) return true // bare handle/username, 3+ chars

        return false
    }

    _freeformRow(q) {
        return `<button type="button" class="composer-recipient-row composer-recipient-row--new" data-action="universal-compose#pickRecipient"
            data-new="true" data-handle="${this._escape(q)}" data-name="${this._escape(q)}">
            <span class="composer-recipient-face composer-recipient-face--initial"><i class="bi bi-person-plus" aria-hidden="true"></i></span>
            <span class="composer-recipient-name">Send to "${this._escape(q)}"</span>
            <span class="composer-recipient-hint">starts a new ${this._escape(this._dest.label)} conversation</span>
        </button>`
    }

    pickRecipient(e) {
        const d = e.currentTarget.dataset
        if (d.new === "true") {
            // No synced chat (or no contact at all — freeform) to point the
            // form at yet; onSubmit routes this recipient through
            // /composer/start_chat instead of a channel_chat's own endpoint.
            this._recipient = { name: d.name, newChat: true, channel: this._dest.channel }
            if (d.contactId) this._recipient.contactId = d.contactId
            if (d.handle) this._recipient.handle = d.handle
            this.element.action = ""
        } else {
            this._recipient = { name: d.name, url: d.url, avatar: d.avatar }
            this.element.action = d.url
        }
        this._closeRecipientPanel()
        this._paintRecipientChip()
        this._syncPlaceholder()
        this._syncSend()
        this.fieldTarget.focus()
    }

    clearRecipient(e) {
        e.stopPropagation()
        this._recipient = null
        this.element.action = ""
        this._paintRecipientChip()
        this._syncPlaceholder()
        this._syncSend()
    }

    _paintRecipientChip() {
        const chip = this.recipientChipTarget
        if (this._recipient) {
            const face = this._recipient.avatar
                ? `<img class="composer-recipient-face" src="${this._escape(this._recipient.avatar)}" alt="" referrerpolicy="no-referrer">`
                : `<span class="composer-recipient-face composer-recipient-face--initial">${this._escape(this._recipient.name[0])}</span>`
            chip.classList.remove("composer-recipient-chip--empty")
            chip.innerHTML = `${face}<span>${this._escape(this._recipient.name)}</span>
                <i class="bi bi-x composer-recipient-x" role="button" aria-label="Remove contact" data-action="click->universal-compose#clearRecipient"></i>`
        } else {
            chip.classList.add("composer-recipient-chip--empty")
            chip.innerHTML = `<i class="bi bi-person-plus" aria-hidden="true"></i><span>Add contact</span>`
        }
    }

    _closeRecipientPanel() {
        if (this.hasRecipientPanelTarget) this.recipientPanelTarget.hidden = true
    }

    // --- sending ---------------------------------------------------------------

    syncSend() { this._syncSend() }

    _syncSend() {
        if (!this.hasSendBtnTarget) return
        const hasText = this.fieldTarget.value.trim().length > 0
        const ready = this._dest.kind === "audience" || (this._dest.kind === "channel" && !!this._recipient)
        this.sendBtnTarget.disabled = !(hasText && ready)
    }

    // Non-Jane sends stay on home: fetch the JSON contract, confirm inline.
    onSubmit(e) {
        if (this._dest.kind === "jane") return
        e.preventDefault()
        if (this.sendBtnTarget.disabled) return

        const newChat = this._dest.kind === "channel" && !!this._recipient?.newChat
        let url, body
        if (newChat) {
            // No chat exists yet — route the first send through
            // Messaging::StartChat instead of a channel_chat messages endpoint.
            url = "/composer/start_chat"
            body = new FormData()
            body.append("provider", this._recipient.channel)
            body.append("body", this.fieldTarget.value)
            if (this._recipient.contactId) body.append("contact_id", this._recipient.contactId)
            if (this._recipient.handle) body.append("handle", this._recipient.handle)
        } else {
            url = this.element.action
            body = new FormData(this.element)
            // The form's embedded token is per-form (scoped to Jane's action), so a
            // swapped destination must authenticate via the session-wide header
            // token instead — same as assistant-compose's settings PATCH.
            body.delete("authenticity_token")
        }

        this.sendBtnTarget.disabled = true
        fetch(url, {
            method: "POST",
            headers: {
                "Accept": "application/json",
                "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
            },
            credentials: "same-origin",
            body
        })
            .then((r) => r.json())
            .then((data) => {
                if (data.ok && data.html) {
                    // Email (ComposersController#email): what was typed is now
                    // the body of an email card - To filled in, a subject to
                    // add, Send with Undo on the card. Nothing has gone yet.
                    this.fieldTarget.value = ""
                    this.element.insertAdjacentHTML("afterend", data.html)
                    this._showStatus(data.notice || "Email draft ready - add a subject and send.", null)
                    this.element.nextElementSibling?.querySelector("input, [contenteditable]")?.focus()
                } else if (data.ok) {
                    this.fieldTarget.value = ""
                    if (newChat && data.channel_chat_id) {
                        // The chat now exists locally — keep the same recipient
                        // but stop routing through start_chat, or a follow-up
                        // send would mint a second, duplicate conversation.
                        this._recipient.newChat = false
                        this.element.action = `/messages/chats/${data.channel_chat_id}/messages`
                    }
                    this._showStatus(this._sentLabel(), data.thread_url)
                } else {
                    this._showStatus(data.error || "Couldn't send — try again.", null, true)
                }
            })
            .catch(() => this._showStatus("Couldn't send — try again.", null, true))
            .finally(() => this._syncSend())
    }

    _sentLabel() {
        if (this._dest.kind === "audience") return `Shared with ${this._dest.label}.`
        const via = this._dest.channel === "taskjane" ? "privately" : `on ${this._dest.label}`
        return `Sent to ${this._recipient.name} ${via}.`
    }

    _showStatus(text, threadUrl, isError = false) {
        if (!this.hasStatusTarget) return
        const link = threadUrl ? ` <a href="${this._escape(threadUrl)}" data-turbo="false">View thread</a>` : ""
        this.statusTarget.innerHTML = `${this._escape(text)}${link}`
        this.statusTarget.classList.toggle("is-error", isError)
        clearTimeout(this._statusTimer)
        if (!isError) this._statusTimer = setTimeout(() => this._clearStatus(), 8000)
    }

    _clearStatus() {
        if (!this.hasStatusTarget) return
        this.statusTarget.innerHTML = ""
        this.statusTarget.classList.remove("is-error")
    }

    _escape(s) {
        return String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    }
}
